import React, {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Button} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import {Loading} from '@pb/kit-component-loading';
import {PaginationChangeHandler, PaginationStripe} from '@pb/kit-component-pagination';
import {Scroll} from '@pb/kit-component-scroll';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {H2, H3} from '@pb/kit-component-typography';

import {SearchCompanies$Request} from '/src/openapi/ccdb-bff/apis/CompaniesApi';

import {DEFAULT_SEARCH_PAGINATION, useCompanySearch} from './useCompanySearch';

import {
    companySearchContainer,
    emptyState,
    errorMessage,
    resultsSection,
    searchBar,
    searchInput,
    statusBadge,
} from './styles.module.styl';

type SearchFieldOption = SearchCompanies$Request['searchField'];

const SEARCH_FIELD_OPTIONS: {value: SearchFieldOption, label: string}[] = [
    {value: 'COMPANY_NAME', label: 'Company Name'},
    {value: 'COMPANY_ID', label: 'Company ID'},
    {value: 'COY_ID', label: 'COY ID'},
    {value: 'COMPANY_CIK', label: 'CIK'},
    {value: 'SYMBOL', label: 'Symbol'},
    {value: 'SHARECLASS_ID', label: 'Share Class ID'},
];

type CompanySearchViewProps = {
    onCompanySelect?: (companyId: string) => void,
};

const CompanySearchView: React.FC<CompanySearchViewProps> = ({onCompanySelect}) => {
    const {searchResults, loading, error, doSearch} = useCompanySearch();
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState('');
    const [searchField, setSearchField] = useState<SearchFieldOption>('COMPANY_ID');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_SEARCH_PAGINATION.size);

    const handleSearch = useCallback(() => {
        if (!searchValue.trim()) {
            return;
        }
        setCurrentPage(0);
        doSearch({
            searchValue: searchValue.trim(),
            searchField,
            page: 0,
            size: pageSize,
        });
    }, [searchValue, searchField, pageSize, doSearch]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);

    const onPaginationChange: PaginationChangeHandler = useCallback(({currentPage: page, pageSize: size}) => {
        setCurrentPage(page);
        setPageSize(size);
        doSearch({
            searchValue: searchValue.trim(),
            searchField,
            page,
            size,
        });
    }, [searchValue, searchField, doSearch]);

    const handleCompanyClick = useCallback((companyId: string) => {
        if (onCompanySelect) {
            onCompanySelect(companyId);
        }
        navigate(`/company-detail/${companyId}`);
    }, [onCompanySelect, navigate]);

    return (
        <div className={companySearchContainer}>
            <H2>CCDB Company Search</H2>

            <Group direction="h" className={searchBar}>
                <div className={searchInput}>
                    <label htmlFor="search-input" style={{display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600}}>
                        Search Value
                    </label>
                    <Input
                        id="search-input"
                        value={searchValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter search value..."
                    />
                </div>
                <div>
                    <label htmlFor="search-field-select" style={{display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600}}>
                        Search Field
                    </label>
                    <select
                        id="search-field-select"
                        value={searchField}
                        onChange={e => setSearchField(e.target.value as SearchFieldOption)}
                        style={{
                            height: 36,
                            padding: '0 12px',
                            borderRadius: 4,
                            border: '1px solid #ccc',
                            fontSize: 14,
                            minWidth: 180,
                        }}>
                        {SEARCH_FIELD_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <Button
                    onClick={handleSearch}
                    disabled={!searchValue.trim() || loading}>
                    Search
                </Button>
            </Group>

            {error ? (
                <div className={errorMessage}>
                    {error}
                </div>
            ) : null}

            {loading ? (
                <Group style={{justifyContent: 'center', padding: 40}}>
                    <Loading/>
                </Group>
            ) : null}

            {!loading && searchResults ? (
                <div className={resultsSection}>
                    <H3>
                        Results: {searchResults.totalElements} companies found
                        {searchResults.totalPages > 1 && ` (Page ${searchResults.page + 1} of ${searchResults.totalPages})`}
                    </H3>

                    {searchResults.companies.length === 0 ? (
                        <div className={emptyState}>
                            <div>🔍</div>
                            <p>No companies found matching your search criteria.</p>
                        </div>
                    ) : (
                        <>
                            <Scroll>
                                <Table>
                                    <Head>
                                        <Cell number={true}>#</Cell>
                                        <Cell>Company ID</Cell>
                                        <Cell>Company Name</Cell>
                                        <Cell>Legal Name</Cell>
                                        <Cell>Short Name / Symbol</Cell>
                                        <Cell>CIK</Cell>
                                        <Cell>Status</Cell>
                                        <Cell>City</Cell>
                                        <Cell>State</Cell>
                                        <Cell>Country ID</Cell>
                                    </Head>
                                    <Body>
                                        {searchResults.companies.map((company, index) => (
                                            <Row key={company.companyId}>
                                                <Cell number={true}>
                                                    {searchResults.page * searchResults.size + index + 1}
                                                </Cell>
                                                <Cell>
                                                    <span
                                                        onClick={() => handleCompanyClick(company.companyId)}
                                                        style={{color: '#0066cc', cursor: 'pointer'}}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={e => e.key === 'Enter' && handleCompanyClick(company.companyId)}>
                                                        {company.companyId}
                                                    </span>
                                                </Cell>
                                                <Cell>{company.companyName}</Cell>
                                                <Cell>{company.legalName || '—'}</Cell>
                                                <Cell>{company.shortName || '—'}</Cell>
                                                <Cell>{company.cik || '—'}</Cell>
                                                <Cell>
                                                    <span className={`${statusBadge} ${company.companyStatus === 'Active' ? 'active' : 'inactive'}`}>
                                                        {company.companyStatus || '—'}
                                                    </span>
                                                </Cell>
                                                <Cell>{company.city || '—'}</Cell>
                                                <Cell>{company.state || '—'}</Cell>
                                                <Cell>{company.countryId ?? '—'}</Cell>
                                            </Row>
                                        ))}
                                    </Body>
                                </Table>
                            </Scroll>

                            <PaginationStripe
                                onPaginationChange={onPaginationChange}
                                currentPage={currentPage}
                                pageSize={pageSize}
                                sizeSwitcherPosition="LEFT"
                                sizes={[20, 50, 100]}
                                totalCount={searchResults.totalElements}
                            />
                        </>
                    )}
                </div>
            ) : null}

            {!loading && !searchResults && !error && (
                <div className={emptyState}>
                    <div>🏢</div>
                    <p>Enter a search value and click Search to find companies in the CCDB database.</p>
                </div>
            )}
        </div>
    );
};

export default CompanySearchView;
